import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { type IntlFormatters } from 'react-intl';

import { Colors } from './colorUtils';
import { BPOST_TRACKING_PREFIX_URL } from './communicationUtils';
import { calendarDateTimeFromTimestamp, type DateTime, getDateTimeDifference } from './dateutils';
import {
  type SignatureDocument,
  type SignatureDocumentResult,
  SignatureDocumentStatus,
  TrackingProvider,
} from './types';

export const getSignatureDocumentTrackingLink = (signatureDocument?: SignatureDocument | null): string | null => {
  // Get tracking record
  const trackingRecord = signatureDocument?.signatureResults.find(
    (result) => result.trackingProvider && result.trackingCode
  );
  if (!trackingRecord) {
    return null;
  }

  if (trackingRecord.trackingProvider === TrackingProvider.BPOST) {
    return `${BPOST_TRACKING_PREFIX_URL}${trackingRecord.trackingCode}`;
  }
  return null;
};

export const getSignatureDocumentTrackingProvider = (signatureDocument?: SignatureDocument | null): string => {
  // Get tracking record
  const trackingRecord = signatureDocument?.signatureResults.find(
    (result) => result.trackingProvider && result.trackingCode
  );
  return trackingRecord?.trackingProvider ?? '';
};

export const getSignatureDocumentStatusColor = (status: SignatureDocumentStatus) => {
  if (status === SignatureDocumentStatus.DRAFT) return Colors.CARROT_ORANGE;
  if (status === SignatureDocumentStatus.PENDING) return Colors.DODGER_BLUE;
  if (status === SignatureDocumentStatus.REJECTED) return Colors.CARNATION_RED;
  if (status === SignatureDocumentStatus.SIGNED) return Colors.TURQUOISE_BLUE;
  return Colors.BLUEY;
};

export enum SignatureDisplayStatus {
  WAITING_FOR_SIGNATURE = 'WAITING_FOR_SIGNATURE',
  PARTIALLY_SIGNED = 'PARTIALLY_SIGNED',
  REJECTED = 'REJECTED',
  SIGNED = 'SIGNED',
}

export const getSignatureDisplayStatus = (
  status: SignatureDocumentStatus,
  signatureResults: SignatureDocumentResult[]
): SignatureDisplayStatus | undefined => {
  switch (status) {
    case SignatureDocumentStatus.PENDING:
    case SignatureDocumentStatus.DRAFT:
      if (isEmpty(signatureResults.filter((result) => !isNil(result.signed)))) {
        return SignatureDisplayStatus.WAITING_FOR_SIGNATURE;
      }
      return SignatureDisplayStatus.PARTIALLY_SIGNED;
    case SignatureDocumentStatus.REJECTED:
      return SignatureDisplayStatus.REJECTED;
    case SignatureDocumentStatus.SIGNED:
      return SignatureDisplayStatus.SIGNED;
    default:
      return undefined;
  }
};

export const signatureDisplayStatusMessageIds: Record<SignatureDisplayStatus, string> = {
  [SignatureDisplayStatus.WAITING_FOR_SIGNATURE]: 'lease.detail.progress.waitingForSignature',
  [SignatureDisplayStatus.PARTIALLY_SIGNED]: 'enums.SignatureDocumentStatus.PARTIALLY_SIGNED',
  [SignatureDisplayStatus.REJECTED]: 'enums.SignatureDocumentStatus.REJECTED',
  [SignatureDisplayStatus.SIGNED]: 'enums.SignatureDocumentStatus.SIGNED',
};

export const getSignatureDocumentStatusText = (
  status: SignatureDocumentStatus,
  signatureResults: SignatureDocumentResult[],
  formatMessage: IntlFormatters['formatMessage']
) => {
  const displayStatus = getSignatureDisplayStatus(status, signatureResults);
  if (isNil(displayStatus)) {
    return '';
  }
  return formatMessage({ id: signatureDisplayStatusMessageIds[displayStatus] });
};

export const SIGNATURE_INVITE_MINIMUM_INTERVAL_MINUTES = 60;

export const SIGNATURE_FILES_MAX_IDS = 1000;

type SignatoryReminderFields = Pick<SignatureDocumentResult, 'signed' | 'openedDate' | 'lastSignInviteSentDate'>;

export const isSignatoryAwaitingSignature = (signatureResult: SignatoryReminderFields): boolean =>
  isNil(signatureResult.signed) && isNil(signatureResult.openedDate) && !isNil(signatureResult.lastSignInviteSentDate);

export const getMinutesSinceLastSignInvite = (
  { lastSignInviteSentDate }: Pick<SignatureDocumentResult, 'lastSignInviteSentDate'>,
  now: DateTime
): number | null =>
  isNil(lastSignInviteSentDate)
    ? null
    : getDateTimeDifference({ start: calendarDateTimeFromTimestamp(lastSignInviteSentDate), end: now }, 'minutes');

export const canRemindSignatoryManually = (signatureResult: SignatoryReminderFields, now: DateTime): boolean => {
  const minutesSinceLastInvite = getMinutesSinceLastSignInvite(signatureResult, now);
  return (
    isSignatoryAwaitingSignature(signatureResult) &&
    !isNil(minutesSinceLastInvite) &&
    minutesSinceLastInvite >= SIGNATURE_INVITE_MINIMUM_INTERVAL_MINUTES
  );
};
